import React, { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { UsmcaCheckbox } from "@/components/UsmcaCheckbox";
import { printUsmca } from "@/lib/usmca";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

// Manual "we shipped it ourselves" checkout. All three inputs are optional —
// the order flips to `fulfilled` regardless. If tracking is provided, we also
// stamp shipping_shipped_at + shipping_tracking_number so the Fulfilled row
// still shows a real reference.
export default function SelfCheckoutDialog({ order, open, onOpenChange, onSuccess }) {
  const [saving, setSaving] = useState(false);
  const [usmca, setUsmca] = useState(false);
  const [form, setForm] = useState({ tracking_number: "", carrier: "", note: "" });

  const submit = async () => {
    setSaving(true);
    if (usmca) printUsmca(order);
    try {
      await api.post(`/orders/${order.id}/self-checkout`, {
        tracking_number: form.tracking_number.trim() || null,
        carrier: form.carrier.trim() || null,
        note: form.note.trim() || null,
      });
      toast.success(`Order marked shipped · ${order.po_number}`);
      if (typeof onSuccess === "function") {
        try { await onSuccess(); } catch (_) { /* still fulfilled */ }
      }
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Self checkout failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="self-checkout-dialog">
        <DialogHeader>
          <DialogTitle>Self Checkout · {order.po_number}</DialogTitle>
          <DialogDescription>
            Mark this order as shipped manually. Tracking, carrier and note are all optional.
            The order will move to <b>Order Fulfilled</b> as soon as you confirm.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 pt-1">
          <div>
            <Label className="text-xs">Tracking # (optional)</Label>
            <Input
              data-testid="sc-tracking"
              placeholder="e.g. 1Z999AA10123456784"
              value={form.tracking_number}
              onChange={(e) => setForm({ ...form, tracking_number: e.target.value })}
            />
          </div>
          <div>
            <Label className="text-xs">Carrier (optional)</Label>
            <Input
              data-testid="sc-carrier"
              placeholder="e.g. Local courier, UPS, hand delivery…"
              value={form.carrier}
              onChange={(e) => setForm({ ...form, carrier: e.target.value })}
            />
          </div>
          <div>
            <Label className="text-xs">Note (optional)</Label>
            <Input
              data-testid="sc-note"
              placeholder="e.g. Customer picked up in person"
              value={form.note}
              onChange={(e) => setForm({ ...form, note: e.target.value })}
            />
          </div>
          <UsmcaCheckbox order={order} checked={usmca} onChange={setUsmca} testid="sc-usmca" />
          <div className="flex gap-2 justify-end pt-2">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={saving}
              data-testid="sc-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={submit}
              disabled={saving}
              className="bg-slate-900 hover:bg-slate-800 text-white"
              data-testid="sc-submit"
            >
              {saving ? "Marking shipped…" : "✓ Mark as Shipped"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
